// Rewritten 2026-04-21 session (2nd pass): full cattle parity.
// sheep_dailys now carries feeds + minerals jsonb (migration 012); feed master
// list is the shared cattle_feed_inputs, scoped to sheep flocks via herd_scope.
// Edit modal has multi-row feed + mineral pickers (mirrors CattleDailysView).
// Card tile shows feed / mineral summary lines. Comments that are just
// "None" / "none" / "0" / "n/a" no longer render as a pill badge.
import React from 'react';
import Button from '../shared/Button.js';
import Segmented from '../shared/Segmented.js';
import {groupColor, StatusChipRow} from '../shared/GroupPillPicker.js';
import {FilterChips, SearchInput} from '../shared/Toolbar.js';
import {matchesDailySearch, sortDailyRowsByDay} from '../cattle/ruminantDailyListControls.js';
import {SHEEP_FLOCK_KEYS} from '../lib/sheepFlockFilters.js';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {recordSeqNavOptions, dailySeqItems} from '../lib/recordSequence.js';
import type {DailyReportRecord} from '../lib/dailyReportsApi.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {buildSheepDailyExportColumns} from '../lib/dailyReportExports.js';
import type {DailyFeedEntry, DailyMineralEntry, DailyReportRow} from '../lib/dailyReportExports.js';
import {printRows} from '../lib/printExport.js';
import {createSavedView, updateSavedView, deleteSavedView} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
// Loaded on the first Add Report click (LazyAdminAddReportModal), not at startup.
import AdminAddReportModal from '../shared/LazyAdminAddReportModal.tsx';
import OperationalListEmptyState from '../shared/OperationalListEmptyState.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import {DailyCardList, feedListVal, voltageVal, check, mortText, commentText} from '../shared/DailyRecordCards.jsx';
import type {DailyPhotoRef} from '../shared/DailyPhotoThumbnails.jsx';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
const SheepDailyPage = React.lazy(() => import('./SheepDailyPage.tsx'));
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {usePaginatedResource} from '../app/data/usePaginatedResource.js';
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import {matchesTeamMemberFilter, teamMemberOptions, teamMemberSelectValue} from '../lib/teamMemberFilter.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import type {AuthIdentity} from '../contexts/AuthContext.jsx';
import {useDailysRecent} from '../contexts/DailysRecentContext.jsx';
import Header from '../shared/Header.jsx';
import {reconcileSheepDailysRecent} from './sheepDailysRecent.js';

const SHEEP_DAILYS_SURFACE_KEY = 'sheep.dailys';
const VALID_SHEEP_DAILY_SOURCE_FILTERS = new Set(['all', 'daily', 'addfeed']);

type ErrorLike = {message?: string};
// The two jsonb arrays this surface writes. `feed_input_id` is the column the
// edit form round-trips; the rest of each entry is the shared export shape.
type SheepDailyFeed = DailyFeedEntry & {feed_input_id?: string | null; is_creep?: boolean | null};
type SheepDailyMineral = DailyMineralEntry & {feed_input_id?: string | null};
// A sheep_dailys row. The read is `select('*')`, so the open record from
// dailyReportsApi supplies the tail and the columns this view names by hand —
// id / date / flock are the three it indexes, sorts and labels by — are stated.
type SheepDailyRecord = DailyReportRecord &
  Omit<DailyReportRow, 'id' | 'date' | 'flock' | 'team_member' | 'source' | 'feeds' | 'minerals' | 'photos'> & {
    id: string;
    date: string;
    flock: string;
    team_member?: string | null;
    source?: string | null;
    feeds?: SheepDailyFeed[] | null;
    minerals?: SheepDailyMineral[] | null;
    photos?: DailyPhotoRef[] | null;
  };
// A saved_views row, reduced to the columns this surface reads. `view_state` is
// jsonb, so its own fields stay `unknown` and the typeof guards below do real
// narrowing rather than restating a claim the type already made.
type SavedViewRow = {
  id: string;
  name: string;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: unknown;
};
// The shape sheepDailysViewState() writes. Declared as the written record
// rather than per-field `unknown` so the restore below stays the exact
// expression its guard pins; the runtime typeof checks it already made still
// run. Same declaration CattleDailysView makes for the same table.
type SavedSheepDailysViewState = {
  fFlock: string;
  fTeam: string;
  fFrom: string;
  fTo: string;
  srcFilter: string;
};

function SheepDailysRouter() {
  const {authState} = useAuth();
  const location = useLocation();
  const dailyId = location.pathname.startsWith('/sheep/dailys/')
    ? location.pathname.slice('/sheep/dailys/'.length) || null
    : null;
  if (dailyId) {
    return React.createElement(
      React.Suspense,
      {
        fallback: React.createElement(
          'div',
          {role: 'status', 'aria-live': 'polite', style: {padding: '2rem', textAlign: 'center'}},
          'Loading Sheep Daily Record…',
        ),
      },
      React.createElement(SheepDailyPage, {
        sb,
        fmt,
        authState,
      }),
    );
  }
  return React.createElement(SheepDailysHub);
}

// "nothing to report" sentinels — don't render these as a comment badge.
// Public-webform placeholder now tells the team to enter "0"; this covers
// both that convention and legacy "None" / "none" / "n/a" entries.
const isSentinelComment = (s: unknown) => {
  if (s == null) return true;
  const t = String(s).trim().toLowerCase();
  return t === '' || t === 'none' || t === '0' || t === 'n/a' || t === 'na' || t === '-';
};

const SheepDailysHub = () => {
  const {authState} = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState'> & {
    authState: AuthIdentity | null;
  };
  const {setSheepDailysRecent} = useDailysRecent();
  const {useState, useEffect, useMemo} = React;
  // The paginated resource owns the immutable server pages. Rows the admin Add
  // Report modal just created are projected over them so they show before the
  // next read. (The delete-hide projection went with the unreachable legacy edit
  // modal, E12a.)
  const [createdRecords, setCreatedRecords] = useState<SheepDailyRecord[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [exportNotice, setExportNotice] = useState('');
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  // R17: Saved Views and Filters are pop-outs off the one toolbar row. Which
  // one is open is transient, like Phase 2's broiler batches toolbar.
  const [openToolPanel, setOpenToolPanel] = useState<'search' | 'savedViews' | 'filters' | 'sort' | 'columns' | null>(
    null,
  );
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const [savedViewNotice, setSavedViewNotice] = useState<Notice | null>(null);
  const myProfileId = authState?.user?.id || null;
  const savedViewsQuery = useSavedViewsData(SHEEP_DAILYS_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;
  const [fFlock, setFFlock] = usePersistentViewState('sheep.dailys.flockFilter', '');
  const [fTeam, setFTeam] = usePersistentViewState('sheep.dailys.teamFilter', '');
  const [fFrom, setFFrom] = usePersistentViewState('sheep.dailys.fromFilter', '');
  const [fTo, setFTo] = usePersistentViewState('sheep.dailys.toFilter', '');
  const [srcFilter, setSrcFilter] = usePersistentViewState('sheep.dailys.sourceFilter', 'all');
  // R17 Search and Sort, kept for the session (L02). Their defaults - no
  // search, newest day first, rows within a day as loaded - are exactly the
  // list as it was. They are not part of the saved-view state.
  const [search, setSearch] = usePersistentViewState('sheep.dailys.search', '');
  const [sortDir, setSortDir] = usePersistentViewState('sheep.dailys.sortDir', 'desc');
  const [sortWithin, setSortWithin] = usePersistentViewState('sheep.dailys.sortWithin', 'loaded');
  // R17 Columns: the card parts this list hides (shared DailyRecordCards
  // hiddenColumns). Empty - every part shown - is the default.
  const [hiddenColumns, setHiddenColumns] = usePersistentViewState('sheep.dailys.hiddenColumns', [] as string[]);

  const FLOCKS = ['rams', 'ewes', 'feeders', 'processed', 'deceased', 'sold'];
  const FLOCK_LABELS: Record<string, string> = {
    rams: 'Rams',
    ewes: 'Ewes',
    feeders: 'Feeders',
    processed: 'Processed',
    deceased: 'Deceased',
    sold: 'Sold',
  };

  // The list's pages, its cursor, its cancellation and its stale-key protection
  // belong to sheep.dailys.list now. `reload` is in the page KEY on purpose: a
  // Retry must start a NEW complete sequence rather than same-key refetch an
  // already-partial list — the contract cattle.dailys.list documents.
  const sheepDailysParams = useMemo(() => ({reload: reloadKey}), [reloadKey]);
  const sheepDailys = usePaginatedResource<SheepDailyRecord, number>(RESOURCE.SHEEP_DAILYS_LIST, sheepDailysParams);
  const {
    error: listError,
    hasMore,
    loadMore,
    loadMoreError: listLoadMoreError,
    loadingMore: listLoadingMore,
  } = sheepDailys;
  const loading = sheepDailys.loading;
  const loadError = useMemo<Notice | null>(
    () =>
      listError
        ? {
            kind: 'error',
            message:
              'Could not load daily reports. Please refresh the page. (' +
              ((listError as ErrorLike).message || String(listError)) +
              ')',
          }
        : null,
    [listError],
  );
  // sheep.dailys.formInputs fed only the legacy edit modal's feed/mineral
  // dropdowns, which had no way in and went in E12a. The read is RETAINED for
  // now because it is a declared resource of the released sheep.dailys.index
  // surface (its receipt and manifest name it); retiring it, receipt included,
  // is listed for E12b. Nothing here reads its payload.
  useRouteResource(RESOURCE.SHEEP_DAILYS_FORM_INPUTS, sheepDailysParams);

  // The server pages with this session's local mutations projected over them.
  // The order is what the old single array produced: newly created rows first,
  // then the pages in server order. De-duplicated by id because the append loop
  // used to.
  const records = useMemo<SheepDailyRecord[]>(() => {
    const seen = new Set<string>();
    const rows: SheepDailyRecord[] = [];
    for (const row of [...createdRecords, ...sheepDailys.rows]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
    return rows;
  }, [createdRecords, sheepDailys.rows]);

  // A Retry is a new server sequence, so its rows are authoritative again and no
  // transient local projection may leak into the new key.
  useEffect(() => {
    setCreatedRecords([]);
  }, [reloadKey]);

  // The eager "load every page" behaviour is unchanged; only the cursor moved.
  // `listLoadMoreError` is in the guard so a failed append stops the loop rather
  // than retrying forever — the old `pgLoading` ref never reset on failure,
  // which had the same effect by accident.
  useEffect(() => {
    if (hasMore && !listLoadingMore && !listLoadMoreError) {
      void loadMore();
    }
  }, [hasMore, listLoadingMore, listLoadMoreError, loadMore]);

  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  const navigate = useNavigate();

  // Split in two so the source chips can count each source under the same
  // date/flock/team filters (R18); the rows shown are exactly what the one
  // combined predicate used to return.
  // Search (L02) narrows like a filter, over fields already loaded: the flock,
  // the team member, the feed and mineral names, and the comments.
  const matchesSearch = (r: SheepDailyRecord) =>
    matchesDailySearch(
      [
        FLOCK_LABELS[r.flock] || r.flock,
        r.team_member,
        ...(Array.isArray(r.feeds) ? r.feeds.map((f) => f.feed_name) : []),
        ...(Array.isArray(r.minerals) ? r.minerals.map((m) => m.name) : []),
        r.comments,
      ],
      search,
    );
  const baseFiltered = records.filter(
    (r) =>
      (!fFlock || r.flock === fFlock) &&
      matchesTeamMemberFilter(r.team_member, fTeam) &&
      (!fFrom || r.date >= fFrom) &&
      (!fTo || r.date <= fTo) &&
      matchesSearch(r),
  );
  const sourceRows = baseFiltered.filter(
    (r) =>
      srcFilter === 'all' ||
      (srcFilter === 'daily' && r.source !== 'add_feed_webform') ||
      (srcFilter === 'addfeed' && r.source === 'add_feed_webform'),
  );
  // Sort keeps the R21 day grouping: DailyCardList groups consecutive same-day
  // rows, so the order is always by day (newest or oldest first) and only the
  // order WITHIN a day is chosen. The default is the loaded order untouched.
  // `filtered` is the sorted set, so the rows, the record sequence and CSV/Print
  // all follow it.
  const filtered = sortDailyRowsByDay(
    sourceRows,
    sortDir,
    sortWithin,
    (r) => String(FLOCK_LABELS[r.flock] || r.flock || ''),
    (r) => String(r.team_member || ''),
  );
  const addFeedCount = baseFiltered.filter((r) => r.source === 'add_feed_webform').length;
  // Counts come from the rows already loaded. The list eagerly loads every
  // page, so they are final once hasMore clears; until then the summary says
  // so rather than presenting a partial count as the whole.
  const sourceChips = [
    {key: 'all', label: 'All', count: baseFiltered.length},
    {key: 'daily', label: 'Daily Reports', count: baseFiltered.length - addFeedCount},
    {key: 'addfeed', label: 'Add Feed', count: addFeedCount},
  ];
  const teamOpts = teamMemberOptions(records.map((r) => r.team_member));
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);

  function sheepDailysViewState() {
    return {
      fFlock: fFlock || '',
      fTeam: fTeam || '',
      fFrom: fFrom || '',
      fTo: fTo || '',
      srcFilter: VALID_SHEEP_DAILY_SOURCE_FILTERS.has(srcFilter) ? srcFilter : 'all',
    };
  }

  function applySheepDailysSavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    const st = (view.view_state || {}) as SavedSheepDailysViewState;
    setFFlock(typeof st.fFlock === 'string' ? st.fFlock : '');
    setFTeam(typeof st.fTeam === 'string' ? st.fTeam : '');
    setFFrom(typeof st.fFrom === 'string' ? st.fFrom : '');
    setFTo(typeof st.fTo === 'string' ? st.fTo : '');
    setSrcFilter(VALID_SHEEP_DAILY_SOURCE_FILTERS.has(st.srcFilter) ? st.srcFilter : 'all');
    setSavedViewNotice(null);
  }

  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    setSavedViewNotice(null);
    if (!id) return;
    applySheepDailysSavedView(savedViews.find((v) => v.id === id));
  }

  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setSavedViewNotice(null);
    setShowSaveViewForm(true);
  }

  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setSavedViewNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: SHEEP_DAILYS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: sheepDailysViewState(),
      })) as {id?: string} | null;
      setShowSaveViewForm(false);
      setSavedViewNotice({kind: 'success', message: 'Saved view "' + name + '".'});
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (caught) {
      const e = caught as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Save view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: sheepDailysViewState()});
      await loadSavedViews();
      setSavedViewNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current filters.',
      });
    } catch (caught) {
      const e = caught as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Update view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setSavedViewNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (caught) {
      const e = caught as ErrorLike;
      setSavedViewNotice({kind: 'error', message: 'Delete view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete saved view "' + selectedView.name + '"?', () => {
      void proceedDeleteSelectedView(selectedView);
    });
  }

  function handleExportCsv() {
    const columns = buildSheepDailyExportColumns({flockLabels: FLOCK_LABELS, isSentinelComment});
    const ok = downloadCsv(csvFilename('sheep-dailys'), rowsToCsv(columns, filtered));
    setExportNotice(ok ? '' : 'CSV export is only available in the browser.');
  }

  function handlePrintRows() {
    const columns = buildSheepDailyExportColumns({flockLabels: FLOCK_LABELS, isSentinelComment});
    const ok = printRows({
      title: 'Sheep Dailys',
      subtitle: filtered.length + ' filtered daily reports',
      columns,
      rows: filtered,
    });
    setExportNotice(ok ? '' : 'Print is only available in the browser.');
  }

  const fi: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    fontSize: 12,
    fontFamily: 'inherit',
    background: 'var(--surface)',
    width: 'auto',
  };
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  const savedViewRadioLabelS: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: 'var(--ink)',
    cursor: 'pointer',
  };
  // The open pop-out panel, and its toggle's open state (Q01: green outline),
  // as Phase 2's broiler batches toolbar draws them.
  const toolPanelS: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '12px 16px',
    marginBottom: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };
  const toolOpenS: React.CSSProperties = {borderColor: 'var(--brand)', color: 'var(--brand)'};
  const filterLabelS: React.CSSProperties = {
    fontSize: 11,
    color: 'var(--ink-muted)',
    display: 'inline-flex',
    flexDirection: 'column',
    gap: 3,
    fontWeight: 600,
  };
  const toggleToolPanel = (panel: 'search' | 'savedViews' | 'filters' | 'sort' | 'columns') =>
    setOpenToolPanel((cur) => (cur === panel ? null : panel));
  // L03: the filters now live in a pop-out, so the active ones show as
  // removable chips under the toolbar.
  const activeFilters = [
    fFrom ? {key: 'from', label: 'From ' + fmt(fFrom), onRemove: () => setFFrom('')} : null,
    fTo ? {key: 'to', label: 'To ' + fmt(fTo), onRemove: () => setFTo('')} : null,
    fFlock ? {key: 'flock', label: FLOCK_LABELS[fFlock] || fFlock, onRemove: () => setFFlock('')} : null,
    fTeam ? {key: 'team', label: fTeam, onRemove: () => setFTeam('')} : null,
  ].filter((f): f is {key: string; label: string; onRemove: () => void} => f !== null);
  // The filter bar's old Clear, unchanged, now behind the chips' Clear All
  // (L03): it resets the source as well. A source-only reset is the All chip.
  function clearAllFilters() {
    setFFlock('');
    setFTeam('');
    setFFrom('');
    setFTo('');
    setSrcFilter('all');
  }

  // Summary metrics derived from feeds jsonb (hay bales + alfalfa lbs)
  // Every String() below is a NO-OP at runtime: parseInt/parseFloat already
  // coerce their argument with ToString before parsing, so a numeric column
  // value parses exactly as it did. They are spelled out because these jsonb
  // columns can arrive typed or as text.
  const totalMort = filtered.reduce((s, r) => s + (parseInt(String(r.mortality_count)) || 0), 0);
  const totalBales = filtered.reduce(
    (s, r) =>
      s +
      (Array.isArray(r.feeds)
        ? r.feeds.reduce(
            (ss, f) => ss + (f.category === 'hay' && f.unit === 'bale' ? parseFloat(String(f.qty)) || 0 : 0),
            0,
          )
        : 0),
    0,
  );
  // Alfalfa pellets only — exclude alfalfa hay so historical hay bales
  // (remapped to the ALFALFA cattle hay entry by migration 013) don't
  // get double-counted in the alfalfa-lb total.
  const totalAlfalfa = filtered.reduce(
    (s, r) =>
      s +
      (Array.isArray(r.feeds)
        ? r.feeds.reduce((ss, f) => {
            const nm = String(f.feed_name || '').toLowerCase();
            return ss + (f.category === 'pellet' && nm.includes('alfalfa') ? parseFloat(String(f.lbs_as_fed)) || 0 : 0);
          }, 0)
        : 0),
    0,
  );

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-sheep-dailys-loaded={loading || loadError ? 'false' : 'true'}
    >
      <Header />
      {/* An H1, the page's one green on its add action (C04, Q06-B) rather
          than a program-colour fill (program colour is never a button, R05),
          and the shared controls. 1180px, as R19 asks. */}
      <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <h1 className="wcf-page-title">Daily Reports</h1>
            <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>
              {records.length.toLocaleString()} total
            </div>
          </div>
          <div style={{display: 'flex', gap: 8}}>
            <Button
              variant="primary"
              onClick={() => {
                setNotice(null);
                setShowAddModal(true);
              }}
            >
              + Add Report
            </Button>
          </div>
        </div>
        {showAddModal && (
          <AdminAddReportModal
            sb={sb}
            formType="sheep"
            onClose={() => setShowAddModal(false)}
            onSaved={(recs: SheepDailyRecord | SheepDailyRecord[]) => {
              const created = Array.isArray(recs) ? recs : [recs];
              setCreatedRecords((p) => [...created, ...p]);
              setSheepDailysRecent((current) =>
                created.reduce(
                  (recent, record) => reconcileSheepDailysRecent(recent, record),
                  current as SheepDailyRecord[],
                ),
              );
            }}
          />
        )}

        {/* R17: one toolbar row - Search, then Saved Views, Filters and Sort
            as pop-outs, then CSV and Print - drawn as Phase 2's broiler batches
            toolbar. Every control works over the rows already loaded. The
            Saved Views toggle keeps the old row's gating: it is not offered
            while the list itself failed to load. */}
        <div
          data-sheep-dailys-toolbar="1"
          style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8}}
        >
          {/* Search is a pop-out like the rest (R17). An active search names
              itself on the button, so it shows while the panel is closed. */}
          <Button
            type="button"
            data-sheep-dailys-search-toggle="1"
            data-sheep-dailys-search-active={search.trim() ? '1' : '0'}
            aria-expanded={openToolPanel === 'search'}
            onClick={() => toggleToolPanel('search')}
            style={openToolPanel === 'search' || search.trim() ? toolOpenS : undefined}
          >
            {search.trim()
              ? 'Search: ' + (search.trim().length > 18 ? search.trim().slice(0, 18) + '…' : search.trim())
              : 'Search'}
          </Button>
          {!loadError && (
            <Button
              type="button"
              data-sheep-dailys-saved-views-toggle="1"
              aria-expanded={openToolPanel === 'savedViews'}
              onClick={() => toggleToolPanel('savedViews')}
              style={openToolPanel === 'savedViews' ? toolOpenS : undefined}
            >
              Saved Views
            </Button>
          )}
          <Button
            type="button"
            data-sheep-dailys-filters-toggle="1"
            aria-expanded={openToolPanel === 'filters'}
            onClick={() => toggleToolPanel('filters')}
            style={openToolPanel === 'filters' ? toolOpenS : undefined}
          >
            {activeFilters.length > 0 ? 'Filters (' + activeFilters.length + ')' : 'Filters'}
          </Button>
          <Button
            type="button"
            data-sheep-dailys-sort-toggle="1"
            aria-expanded={openToolPanel === 'sort'}
            onClick={() => toggleToolPanel('sort')}
            style={openToolPanel === 'sort' ? toolOpenS : undefined}
          >
            Sort
          </Button>
          <Button
            type="button"
            data-sheep-dailys-columns-toggle="1"
            aria-expanded={openToolPanel === 'columns'}
            onClick={() => toggleToolPanel('columns')}
            style={openToolPanel === 'columns' ? toolOpenS : undefined}
          >
            {hiddenColumns.length > 0 ? 'Columns (' + hiddenColumns.length + ' hidden)' : 'Columns'}
          </Button>
          <Button
            type="button"
            data-sheep-dailys-export-csv="1"
            onClick={handleExportCsv}
            disabled={loading || !!loadError}
          >
            CSV
          </Button>
          <Button type="button" data-sheep-dailys-print="1" onClick={handlePrintRows} disabled={loading || !!loadError}>
            Print
          </Button>
        </div>

        {!loadError && openToolPanel === 'savedViews' && (
          <div data-sheep-dailys-saved-views-row style={toolPanelS}>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-sheep-dailys-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-sheep-dailys-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...fi, minWidth: 200}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        type="button"
                        data-sheep-dailys-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-sheep-dailys-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  {savedViewNotice && (
                    <span
                      style={{
                        fontSize: 12,
                        color: savedViewNotice.kind === 'success' ? 'var(--ok-ink)' : 'var(--danger-ink)',
                      }}
                    >
                      {savedViewNotice.message}
                    </span>
                  )}
                  <Button
                    type="button"
                    data-sheep-dailys-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy || savedViewsLoading}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>
            {showSaveViewForm && (
              <div
                data-sheep-dailys-saved-view-form
                style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}
              >
                <input
                  data-sheep-dailys-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...fi, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveSheepDailysViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-sheep-dailys-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveSheepDailysViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-sheep-dailys-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button
                  type="button"
                  data-sheep-dailys-saved-view-save
                  onClick={submitSaveView}
                  disabled={savedViewBusy}
                >
                  Save
                </Button>
                <Button type="button" onClick={() => setShowSaveViewForm(false)} disabled={savedViewBusy}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        {openToolPanel === 'filters' && (
          <div data-sheep-dailys-filters="1" style={toolPanelS}>
            <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end'}}>
              <label style={filterLabelS}>
                From
                <input
                  type="date"
                  value={fFrom}
                  onChange={(e) => setFFrom(e.target.value)}
                  style={{...fi, width: 140}}
                />
              </label>
              <label style={filterLabelS}>
                To
                <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={{...fi, width: 140}} />
              </label>
              <label style={filterLabelS}>
                Flock
                <select value={fFlock} onChange={(e) => setFFlock(e.target.value)} style={fi}>
                  <option value="">All flocks</option>
                  {FLOCKS.map((f) => (
                    <option key={f} value={f}>
                      {FLOCK_LABELS[f]}
                    </option>
                  ))}
                </select>
              </label>
              <label style={filterLabelS}>
                Team member
                <select
                  value={teamMemberSelectValue(fTeam, teamOpts)}
                  onChange={(e) => setFTeam(e.target.value)}
                  style={fi}
                  data-sheep-dailys-team-filter="1"
                >
                  <option value="">All team</option>
                  {teamOpts.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}

        {openToolPanel === 'search' && (
          <div data-sheep-dailys-search="1" style={toolPanelS}>
            <SearchInput
              value={search}
              onChange={setSearch}
              label="Search daily reports"
              placeholder="Search flock, team, feed, comments"
            />
          </div>
        )}

        {openToolPanel === 'columns' && (
          <div data-sheep-dailys-columns="1" style={toolPanelS}>
            {/* Only parts the card actually draws: its DAILY_COLS keys (feed,
                volt) and its two fixed parts (team, checks). Mortality and the
                comment are row annotations (R21) and always show. CSV/Print
                keep their own columns. */}
            <div style={{display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center'}}>
              {(
                [
                  ['team', 'Team member'],
                  ['feed', 'Feed'],
                  ['volt', 'Fence'],
                  ['checks', 'Water check'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} style={savedViewRadioLabelS}>
                  <input
                    type="checkbox"
                    data-sheep-dailys-column-toggle={key}
                    checked={!hiddenColumns.includes(key)}
                    onChange={(e) =>
                      setHiddenColumns((cur) =>
                        e.target.checked ? cur.filter((k) => k !== key) : [...cur.filter((k) => k !== key), key],
                      )
                    }
                  />
                  {label}
                </label>
              ))}
              <Button
                type="button"
                data-sheep-dailys-columns-reset="1"
                onClick={() => setHiddenColumns([])}
                disabled={hiddenColumns.length === 0}
              >
                Show all
              </Button>
            </div>
          </div>
        )}

        {openToolPanel === 'sort' && (
          <div data-sheep-dailys-sort="1" style={toolPanelS}>
            <div style={{display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end'}}>
              {/* Days stay grouped (R21): the date direction is a two-option
                  choice (R29), and only the order within a day is open. */}
              <div style={filterLabelS}>
                Days
                <Segmented
                  aria-label="Day order"
                  value={sortDir}
                  onChange={setSortDir}
                  options={[
                    {value: 'desc', label: 'Newest first'},
                    {value: 'asc', label: 'Oldest first'},
                  ]}
                />
              </div>
              <label style={filterLabelS}>
                Within a day
                <select
                  value={sortWithin}
                  onChange={(e) => setSortWithin(e.target.value)}
                  style={fi}
                  data-sheep-dailys-sort-within="1"
                >
                  <option value="loaded">As entered</option>
                  <option value="group">Flock</option>
                  <option value="team">Team member</option>
                </select>
              </label>
            </div>
          </div>
        )}

        <FilterChips filters={activeFilters} onClearAll={clearAllFilters} />

        {/* R18: the counted source chips replace the Source segmented control;
            selected is a brand outline. Same srcFilter state, default and
            saved-view round trip as before. */}
        <div style={{marginBottom: 12}}>
          <StatusChipRow
            chips={sourceChips}
            selected={srcFilter}
            onSelect={setSrcFilter}
            rootAttrs={{'data-sheep-dailys-source-chips': '1'}}
            summary={
              <span data-sheep-dailys-count="1">
                {filtered.length.toLocaleString() + (filtered.length === 1 ? ' report' : ' reports')}
                {hasMore ? ' loaded so far' : ''}
              </span>
            }
          />
        </div>
        {exportNotice && <div style={{marginBottom: 14, color: 'var(--danger-ink)', fontSize: 12}}>{exportNotice}</div>}

        <InlineNotice notice={loadError} />
        {loadError && (
          <Button
            type="button"
            data-daily-list-retry="1"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{marginBottom: 12}}
          >
            Retry
          </Button>
        )}
        <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
        {loading && <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading{'…'}</div>}
        <OperationalListEmptyState
          loading={loading}
          loadError={loadError}
          totalCount={records.length}
          filteredCount={filtered.length}
          emptyLabel="No sheep daily reports yet"
        />
        {!loading && !loadError && filtered.length > 0 && (
          <DailyCardList
            program="sheep"
            rows={filtered}
            hiddenColumns={hiddenColumns}
            fmt={fmt}
            maxInitialRows={100}
            onOpen={(d) => navigate('/sheep/dailys/' + d.id, recordSeqNavOptions(dailySeqItems(filtered, 'flock')))}
            rowAttrs={(d) => ({'data-daily-row': d.id})}
            mapRow={(d) => {
              const row = d as SheepDailyRecord;
              // R06: the flock dot is the fixed group palette, index = the
              // flock's position in SHEEP_FLOCK_KEYS, so a row matches the
              // flocks list. An outcome flock (processed/deceased/sold) is not
              // one of the groups and takes the faint neutral, as cattle does.
              const flockIndex = SHEEP_FLOCK_KEYS.indexOf(row.flock);
              return {
                name: FLOCK_LABELS[row.flock] || row.flock,
                dot: flockIndex >= 0 ? groupColor(flockIndex) : 'var(--ink-faint)',
                team: row.team_member || '—',
                source: row.source,
                photos: row.photos,
                vals: {
                  feed: feedListVal(row.feeds, row.minerals),
                  volt: voltageVal(row.fence_voltage_kv),
                },
                checks: [check('Water', row.waterers_working !== false)],
                mort: mortText(row.mortality_count, null),
                comment: isSentinelComment(row.comments) ? null : commentText(row.comments),
              };
            }}
          />
        )}
        {hasMore && (
          <div style={{textAlign: 'center', padding: '0.5rem', fontSize: 11, color: 'var(--ink-faint)'}}>
            Loading more records{'…'}
          </div>
        )}
      </div>
    </div>
  );
};

export default SheepDailysRouter;
